import Image from '@atoms/Image/Image';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { IImage } from '@models/IImage';
import {
  IProductCategoriesAndOptions,
  SelectedCategoriesAndOptions,
} from '@models/IProductCategoriesAndOptions';
import { IProductVariant } from '@models/IProductVariant';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import CategoriesAndOptionsSelector from './CategoriesAndOptionsSelector';

interface Props {
  readonly categoriesAndOptions?: IProductCategoriesAndOptions;
  readonly currentVariation?: IProductVariant;
  readonly dataTestId?: string;
  readonly isProductDetailsLoading?: boolean;
  readonly mainProductImage: IImage;
  readonly selectOptionLabel: string;
  readonly variations: IProductVariant[];
  readonly yourSelectionLabel: string;
  readonly onGetProductData: (
    materialCode: string,
    updateUrl?: boolean
  ) => Promise<void>;
}

const ProductVariationSelector = ({
  categoriesAndOptions,
  currentVariation,
  dataTestId = 'Product-Variation-Selector',
  isProductDetailsLoading,
  mainProductImage,
  selectOptionLabel,
  variations,
  yourSelectionLabel,
  onGetProductData,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const [
    currentCategoriesAndOptionsSelection,
    setCurrentCategoriesAndOptionsSelection,
  ] = useState<SelectedCategoriesAndOptions>({});

  const hasCategoriesAndOptionsSelector =
    !!categoriesAndOptions?.customisationCategories?.length;
  const hasColourOptions = !!categoriesAndOptions?.customisationCategories.some(
    (category) =>
      category.options.some((option) => !!option.colour || !!option.thumbnail)
  );

  useEffect(() => {
    const getCategoriesAndOptionsSelection =
      (): SelectedCategoriesAndOptions => {
        if (!hasCategoriesAndOptionsSelector) return {};

        const { customisationCategories } = categoriesAndOptions;
        const hasSingleCustomisationCategory =
          customisationCategories.length === 1;

        if (hasSingleCustomisationCategory) {
          const selectedOption = customisationCategories[0]?.options.find(
            (option) => option.selected
          );
          return selectedOption
            ? {
                [`${customisationCategories[0]?.categoryId}`]: {
                  categoryName: customisationCategories[0]?.categoryName || '',
                  optionId: selectedOption.materialCode,
                  optionName: selectedOption.name,
                },
              }
            : {};
        }

        return customisationCategories.reduce((acc, item) => {
          const selectedOption = item.options.find((option) => option.selected);

          return selectedOption
            ? {
                ...acc,
                [`${item.categoryId}`]: {
                  categoryName: item.categoryName,
                  optionId: selectedOption.optionId,
                  optionName: selectedOption.name,
                },
              }
            : acc;
        }, {});
      };

    const newSelection = getCategoriesAndOptionsSelection();
    setCurrentCategoriesAndOptionsSelection(newSelection);
  }, [hasCategoriesAndOptionsSelector, categoriesAndOptions]);

  const currentSelectionValues = Object.values(
    currentCategoriesAndOptionsSelection
  ).filter((item) => item);
  const selectionSummaryText = [
    currentVariation?.name,
    ...(currentSelectionValues.length === 1
      ? [`${currentSelectionValues[0]?.optionName}`]
      : currentSelectionValues.map(
          (item) => `${item?.categoryName} ${item?.optionName}`
        )),
  ]
    .filter((value) => value)
    .join(', ');

  return (
    <div
      className="flex w-full flex-col max-lg:gap-y-10"
      data-testid={dataTestId}
    >
      <div className="relative flex flex-col gap-y-7.5 lg:gap-y-15">
        {isProductDetailsLoading && isMobile && (
          <LoadingSpinner extraClasses="absolute z-[1] h-full w-full" />
        )}

        {variations.length > 1 && (
          <div data-testid={`${dataTestId}-Options`}>
            <p
              className="body-two mb-4 lg:mb-5"
              data-testid={`${dataTestId}-Options-Title`}
            >
              {selectOptionLabel}
            </p>

            <div
              className="flex flex-col gap-y-4"
              data-testid={`${dataTestId}-Options-List`}
            >
              {variations.map(({ skuCode, name, pricingData }, index) => {
                const isSelectedOption = skuCode === currentVariation?.skuCode;

                return (
                  <button
                    type="button"
                    key={skuCode}
                    className={classNames(
                      'flex items-center gap-2.5 self-stretch rounded-[10px] border-[1.5px] p-4 transition-colors',
                      isSelectedOption
                        ? 'border-grey-500 bg-grey-100'
                        : 'border-grey-200'
                    )}
                    onClick={() => {
                      updateDataLayer({
                        event: 'productModification',
                        content_type: 'configuration',
                        selection: name,
                      });

                      onGetProductData(skuCode, true);
                    }}
                    data-testid={`${dataTestId}-Options-List-Item-${index}`}
                  >
                    <span
                      className="body-three grow text-left"
                      data-testid={`${dataTestId}-Options-List-Item-${index}-Name`}
                    >
                      {name}
                    </span>

                    {pricingData && (
                      <div className="flex flex-wrap justify-end gap-x-2.5 text-right">
                        {pricingData.formattedCalculatedPrice && (
                          <p
                            className="body-three body-three--bold text-black-100"
                            data-testid={`${dataTestId}-Options-List-Item-${index}-Formatted-Calculated-Price`}
                          >
                            {pricingData.formattedCalculatedPrice}
                          </p>
                        )}

                        {pricingData.amountSaved > 0 &&
                          pricingData.formattedPrice && (
                            <span
                              className="body-three body-three--bold text-grey-500 line-through"
                              data-testid={`${dataTestId}-Options-List-Item-${index}-Formatted-Price`}
                            >
                              {pricingData.formattedPrice}
                            </span>
                          )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {hasCategoriesAndOptionsSelector &&
          hasColourOptions &&
          variations.length > 1 && (
            <AnimatePresence mode="wait">
              <motion.div
                key={mainProductImage?.desktop?.url}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="rounded-[10px] bg-grey-100 px-2.5 pt-[46px] pb-[57px] md:pb-[66px] lg:hidden"
              >
                <Image
                  image={mainProductImage}
                  imageClasses="object-contain h-[210px] md:h-[490px] lg:h-[770px]"
                  dataTestId={`${dataTestId}-Image`}
                />
              </motion.div>
            </AnimatePresence>
          )}

        {hasCategoriesAndOptionsSelector && (
          <CategoriesAndOptionsSelector
            currentCategoriesAndOptionsSelection={
              currentCategoriesAndOptionsSelection
            }
            dataTestId={dataTestId}
            mainProductImage={mainProductImage}
            showMainProductImage={variations.length <= 1}
            onOptionButtonClick={(
              categoriesAndOptionsSelection,
              materialCode
            ) => {
              setCurrentCategoriesAndOptionsSelection(
                categoriesAndOptionsSelection
              );

              if (materialCode) onGetProductData(materialCode, true);
            }}
            {...categoriesAndOptions}
          />
        )}

        {selectionSummaryText && (
          <div data-testid={`${dataTestId}-Current-Selection`}>
            <p className="body-three mb-2.5">{yourSelectionLabel}</p>
            <p className="h6">{selectionSummaryText}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductVariationSelector;
